import {useTranslation} from 'react-i18next';

import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {Box, Typography} from '@mui/material';

import {surveyStatisticBars} from './SurveyStatisticBars.colors';
import {SurveyStatisticOmittedSection} from './SurveyStatisticOmittedSection';
import {TextOverflowWithTooltip} from '../../../../../../../components/TextOverflowWithTooltip/TextOverflowWithTooltip';
import {greenColors, neutral} from '../../../../../../../layouts/theme/designSystem';
import {CompanyLocationSurvey} from '../../../types/SurveyTypes';

interface Props {
  locations?: CompanyLocationSurvey[];
  isOmitted?: boolean;
}

export const LocationDistributionBars = ({locations = [], isOmitted = false}: Props) => {
  const {t} = useTranslation();
  const totalRespondents = locations.reduce((sum, location) => sum + (location.respondents ?? 0), 0);
  const hasLocations = locations.length > 0 && totalRespondents > 0;

  if (isOmitted) {
    return (
      <SurveyStatisticOmittedSection
        title={t('companySurveys.details.locations')}
        icon={<LocationOnOutlinedIcon sx={{fontSize: 14}} />}
        message={t('companySurveys.details.notCollectedInSurvey')}
      />
    );
  }

  if (!hasLocations) return null;

  const getPercentage = (respondents?: number) => {
    if (!respondents || totalRespondents <= 0) return 0;
    return Math.round((respondents / totalRespondents) * 100);
  };

  return (
    <Box>
      <Box
        sx={{
          alignItems: 'center',
          color: greenColors[700],
          display: 'flex',
          gap: '5px',
          marginBottom: '12px',
        }}
      >
        <LocationOnOutlinedIcon sx={{fontSize: 14}} />
        <Typography
          sx={{
            color: 'inherit',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            lineHeight: 1.4,
            textTransform: 'uppercase',
          }}
        >
          {t('companySurveys.details.locations')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: '12px',
          gridTemplateColumns: '1fr',
        }}
      >
        {locations.map((location) => {
          const respondents = location.respondents ?? 0;
          const percentage = getPercentage(respondents);
          const locationName = location.location || '---';

          return (
            <Box
              key={locationName}
              sx={{
                alignItems: 'center',
                display: 'grid',
                gap: '8px',
                gridTemplateColumns: 'minmax(90px, 150px) minmax(0, 1fr) 42px',
              }}
            >
              <TextOverflowWithTooltip
                variant='body1'
                text={locationName}
                sxTypography={{
                  color: neutral[700],
                  fontSize: '12px',
                  lineHeight: 1.4,
                }}
              />
              <Box
                sx={{
                  backgroundColor: surveyStatisticBars.track,
                  borderRadius: '99px',
                  height: '6px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: surveyStatisticBars.fill,
                    borderRadius: '99px',
                    height: '100%',
                    width: `${percentage}%`,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  color: percentage > 0 ? greenColors[700] : neutral[500],
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: 1.4,
                  textAlign: 'right',
                }}
              >
                {percentage}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
